import { LegacyForm } from "PFApp/components/form/legacy_form";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import ProfileEditPanel from "PFApp/profiles/edit/profile_edit_panel";
import { getProfileSectionText } from "PFApp/profiles/edit/profile_edit_sections";
import css from "PFApp/profiles/edit/sections/section.module.scss";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { SearchableVariant, Select } from "PFComponents/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { isAdmin } from "PFCore/helpers/profile";
import { fetchPermissionsGroups } from "PFCore/services/admin/permissions_groups/fetch_permissions_groups";
import { editProfile } from "PFCore/services/admin/profiles/edit_profile";
import { Option } from "PFTypes";
import { AdminProfile, PermissionsGroup } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { camelize, humanize } from "underscore.string";

type AdminCredentialsSectionProps = {
  profile: AdminProfile;
  handleProfileUpdate: () => void;
};

export const AdminCredentialsSection = ({ profile, handleProfileUpdate }: AdminCredentialsSectionProps) => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("profiles", { keyPrefix: "edit.sections.admin" });

  const profileIsAdmin = isAdmin(profile);

  const employeeStatusOptions = useMemo(
    () =>
      ["active", "leave of absence", "withdrawn", "terminated"].map((status) => ({
        id: status,
        value: status,
        displayElement: t(`credentials.statuses.${camelize(status)}` as unknown as TemplateStringsArray, {
          defaultValue: humanize(status)
        }),
        original: { text: status }
      })),

    [t]
  );

  const adminEmailsOptions = useMemo(
    () =>
      ["Receive", "Don't Receive"].map((status) => ({
        id: status,
        value: status,
        original: { text: status }
      })),
    []
  );

  const [state, setState] = useState({
    forgotState: null,
    emailErrors: {} as Record<string, string>,
    employeeStatusErrors: {},
    adminEmailsError: {}
  });

  const [formData, setFormData] = useState({
    email: profile.email,
    employeeStatus: profile.employeeStatus,
    permissionsGroup: { text: profile.permissionsGroup.displayAs, id: profile.permissionsGroup.id },
    adminEmails: profile.adminEmails
  });

  const handleEditProfile = () => editProfile(profile.id, formData);

  const handleEmailSuccess = () => {
    handleProfileUpdate();
    setState((prevState) => ({ ...prevState, emailErrors: {} }));
    showGrowl({ message: t("credentials.successes.email") });
  };

  const handleStatusSuccess = () => {
    handleProfileUpdate();
    setState((prevState) => ({ ...prevState, employeeStatusErrors: {} }));
    showGrowl({ message: t("credentials.successes.employeeStatus") });
  };

  const handleStatusError = (errors) => {
    setState((prevState) => ({ ...prevState, employeeStatusErrors: errors }));
  };

  const handlePermissionsGroupSuccess = () => {
    handleProfileUpdate();
    showGrowl({ message: t("credentials.successes.permissionsGroup") });
  };

  const handleEmailError = (errors) => {
    setState((prevState) => ({ ...prevState, emailErrors: errors }));
  };

  const handleAdminEmailsSuccess = () => {
    handleProfileUpdate();
    setState((prevState) => ({ ...prevState, adminEmailsErrors: {} }));
    showGrowl({ message: t("credentials.successes.adminEmails") });
  };

  const handleAdminEmailsError = (errors) => {
    setState((prevState) => ({ ...prevState, adminEmailsErrors: errors }));
  };

  return (
    <ProfileEditPanel title={getProfileSectionText("admin_credentials", true)}>
      <h3 className={css.header}>{t("credentials.changeEmail")}</h3>
      <LegacyForm
        qaIdPrefix="profile-edit-credentials-email"
        onSuccess={handleEmailSuccess}
        onError={handleEmailError}
        onSubmit={handleEditProfile}
        isDirty={profile.email !== formData.email}
        blockNavigationOnChange
      >
        <div data-qa-id="profile-edit-credentials-email">
          <InputFieldSet
            label={t("credentials.emailAddress")}
            error={state.emailErrors.email}
            tip={t("credentials.changeEmailTip")}
            value={formData.email}
            onChange={(value) => setFormData({ ...formData, email: value })}
          />
        </div>
      </LegacyForm>

      <h3 className={css.header}>{t("credentials.changeEmployeeStatus")}</h3>

      <LegacyForm
        qaIdPrefix="profile-edit-credentials-employee-status"
        onSuccess={handleStatusSuccess}
        onError={handleStatusError}
        onSubmit={handleEditProfile}
        isDirty={profile.employeeStatus !== formData.employeeStatus}
        blockNavigationOnChange
      >
        <div data-qa-id="profile-edit-credentials-employee-status">
          <Select<{ text: string }>
            options={employeeStatusOptions}
            label={t("credentials.employeeStatus")}
            error={(state.employeeStatusErrors?.[0] || "").toString()}
            value={formData.employeeStatus}
            onChange={(option: Option<{ text: string }>) => {
              setFormData({ ...formData, employeeStatus: option.original.text });
            }}
          />
        </div>
      </LegacyForm>

      <h3 className={css.header}>{t("credentials.changePermissionGroup")}</h3>
      <LegacyForm
        qaIdPrefix="profile-edit-credentials-permission-group"
        onSubmit={handleEditProfile}
        onSuccess={handlePermissionsGroupSuccess}
        isDirty={profile.permissionsGroup.id !== formData.permissionsGroup.id}
        blockNavigationOnChange
      >
        <Select<PermissionsGroup>
          fetchOptions={({ ids }) => {
            const promise = ids
              ? Promise.resolve({ entries: [formData.permissionsGroup], meta: { total: 1 } })
              : fetchPermissionsGroups({ page: 1, perPage: PER_PAGE_UNPAGINATED });

            return promise.then((data) => ({
              ...data,
              entries: (data.entries || []).map((entry) => ({
                id: entry.id,
                value: entry.displayAs || entry.text,
                original: entry
              }))
            }));
          }}
          label={t("credentials.permissionGroup")}
          searchableVariant={SearchableVariant.Never}
          value={formData.permissionsGroup.id}
          onChange={(option: Option<PermissionsGroup>) => {
            setFormData({
              ...formData,
              permissionsGroup: { text: option.value, id: Number(option.id) }
            });
          }}
        />
      </LegacyForm>

      {profileIsAdmin && (
        <React.Fragment>
          <h3 className={css.header}>Change Admin Email settings</h3>
          <LegacyForm
            qaIdPrefix="profile-edit-credentials-admin-emails"
            onSubmit={handleEditProfile}
            onSuccess={handleAdminEmailsSuccess}
            onError={handleAdminEmailsError}
            isDirty={profile.adminEmails !== formData.adminEmails}
            blockNavigationOnChange
          >
            <div data-qa-id="profile-edit-credentials-admin-emails">
              {/* TODO: [PROF-3790] i18n */}
              <Select<{ text: string }>
                options={adminEmailsOptions}
                label={t("credentials.adminEmails")}
                value={formData.adminEmails ? "Receive" : "Don't Receive"}
                onChange={(option: Option<{ text: string }>) => {
                  setFormData({ ...formData, adminEmails: option!.original.text === "Receive" });
                }}
                tip="If set to 'Receive' the user will get admin emails like Access Requests etc."
              />
            </div>
          </LegacyForm>
        </React.Fragment>
      )}
    </ProfileEditPanel>
  );
};
